/**
 * Metro Ministries Admin Dashboard - Nonprofit Program Variant
 *
 * Standalone dashboard for Metropolitan Ministries (and similar nonprofits)
 * that reframes school analytics into program impact metrics.
 *
 * Key differences from SchoolAdminDashboard:
 * - "Students" → "Participants", "Teachers" → "Mentors"
 * - "Seats" → "Participant Slots", "Grade" → "Program Track"
 * - ROI framed as funder impact, not school board budget justification
 * - Highlights adult learner + ESL + youth program tracks
 * - Independence & engagement as self-sufficiency outcomes
 *
 * Calls the same /api/admin/school/* endpoints (session-based)
 * but presents data with nonprofit framing.
 *
 * Route: /admin/program/* (nonprofit_admin role)
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from '../utils/authInterceptor';

// Official Tappy Brand Colors
const C = {
  darkTeal: '#1A3E48',
  teal: '#297A92',
  mediumTeal: '#388899',
  brandBlue: '#76909F',
  brightTeal: '#42909E',
  aqua: '#4D99A5',
  skyBlue: '#6DB4CD',
  coral: '#EA6D37',
  gold: '#ECC962',
  brightGold: '#F8C341',
  darkSlate: '#3D4954',
  mauve: '#8D7F88',
  mintGray: '#ABCACA',
  lightGray: '#D5DEDD',
  paleGray: '#ECF3F2',
  offWhite: '#F5F9FA',
  nearWhite: '#FCFDFE',
};

// ─── Reusable sub-components ────────────────────────────────────────────────

const MetricCard = ({ value, label, sublabel, percentage, trend, alert, accent }) => (
  <div style={{
    backgroundColor: 'white',
    padding: '28px',
    borderRadius: '16px',
    border: `2px solid ${C.lightGray}`,
    borderTop: accent ? `4px solid ${accent}` : `2px solid ${C.lightGray}`,
    boxShadow: '0 1px 3px rgba(26,62,72,0.08)',
    transition: 'all 200ms cubic-bezier(0.34,1.56,0.64,1)',
    cursor: 'default',
  }}
    onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 16px rgba(26,62,72,0.12)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = '0 1px 3px rgba(26,62,72,0.08)'; e.currentTarget.style.transform = 'translateY(0)'; }}
  >
    <div style={{ fontSize: '36px', fontWeight: 700, color: alert ? C.coral : (accent || C.teal), marginBottom: '10px' }}>{value}</div>
    <div style={{ fontSize: '13px', fontWeight: 600, color: C.brandBlue, marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
    {sublabel && <div style={{ fontSize: '12px', color: C.mauve }}>{sublabel}</div>}
    {percentage !== undefined && (
      <div style={{ marginTop: '12px', height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${Math.min(percentage, 100)}%`, backgroundColor: percentage >= 80 ? '#10b981' : percentage >= 50 ? '#f59e0b' : '#ef4444', transition: 'width 0.3s' }} />
      </div>
    )}
    {trend && <div style={{ marginTop: '8px', fontSize: '12px', color: trend === 'up' ? '#10b981' : trend === 'down' ? '#ef4444' : '#6b7280' }}>{trend === 'up' ? '↑' : trend === 'down' ? '↓' : '→'} {trend}</div>}
  </div>
);

const SectionCard = ({ title, subtitle, children, style }) => (
  <div style={{
    backgroundColor: 'white', padding: '32px', borderRadius: '16px',
    border: `2px solid ${C.lightGray}`, boxShadow: '0 1px 3px rgba(26,62,72,0.08)',
    marginBottom: '24px', ...style,
  }}>
    {title && <h3 style={{ fontSize: '20px', fontWeight: 600, color: C.darkTeal, marginBottom: subtitle ? '6px' : '20px', letterSpacing: '-0.3px' }}>{title}</h3>}
    {subtitle && <p style={{ fontSize: '14px', color: C.brandBlue, marginBottom: '20px', lineHeight: '1.5' }}>{subtitle}</p>}
    {children}
  </div>
);

const InfoBox = ({ color, children }) => (
  <div style={{
    padding: '16px 20px', borderRadius: '10px', marginTop: '16px',
    backgroundColor: color === 'green' ? 'rgba(16,185,129,0.06)' : color === 'blue' ? 'rgba(41,122,146,0.06)' : 'rgba(236,201,98,0.08)',
    border: `1px solid ${color === 'green' ? 'rgba(16,185,129,0.15)' : color === 'blue' ? 'rgba(41,122,146,0.15)' : 'rgba(236,201,98,0.2)'}`,
  }}>
    <div style={{ fontSize: '13px', color: C.darkSlate, lineHeight: '1.6' }}>{children}</div>
  </div>
);

const Badge = ({ label, color }) => (
  <span style={{
    display: 'inline-block', padding: '4px 12px', borderRadius: '10px',
    fontSize: '11px', fontWeight: 600, letterSpacing: '0.3px',
    backgroundColor: `${color}15`, color: color, border: `1px solid ${color}30`,
  }}>{label}</span>
);

// ─── Helper: classify independence ────────────────────────────────────────────
const independenceLabel = (score) => {
  if (score >= 70) return { text: 'Self-Directed', color: '#10b981' };
  if (score >= 40) return { text: 'Growing', color: '#f59e0b' };
  return { text: 'Needs Support', color: '#ef4444' };
};

// ─── Main Component ────────────────────────────────────────────────────────────

function MetroMinistriesDashboard({ user, onLogout }) {
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overview, setOverview] = useState(null);
  const [students, setStudents] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [teachingQuality, setTeachingQuality] = useState(null);
  const [reteachRoi, setReteachRoi] = useState(null);
  const [safety, setSafety] = useState(null);

  const api = useCallback((endpoint) => `/api/admin/school/${endpoint}`, []);

  // ── Data fetching ──────────────────────────────────────────────────────────
  const fetchOverview = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(api('overview'));
      setOverview(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [api]);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(api('students'));
      setStudents(res.data);
    } catch (e) { console.error('students fetch error', e); }
  }, [api]);

  const fetchAnalytics = useCallback(async () => {
    try {
      const [analyticsRes, tqRes, roiRes] = await Promise.all([
        axios.get(api('analytics')),
        axios.get(api('teaching-quality')).catch(() => ({ data: null })),
        axios.get(api('reteach-roi')).catch(() => ({ data: null })),
      ]);
      setAnalytics(analyticsRes.data);
      setTeachingQuality(tqRes.data);
      setReteachRoi(roiRes.data);
    } catch (e) { console.error('analytics fetch error', e); }
  }, [api]);

  const fetchSafety = useCallback(async () => {
    try {
      const res = await axios.get(api('safety'));
      setSafety(res.data);
    } catch (e) { console.error('safety fetch error', e); }
  }, [api]);

  useEffect(() => { fetchOverview(); }, [fetchOverview]);

  useEffect(() => {
    if (activeTab === 'participants' && !students) fetchStudents();
    if (activeTab === 'impact' && !analytics) fetchAnalytics();
    if (activeTab === 'safety' && !safety) fetchSafety();
  }, [activeTab, students, analytics, safety, fetchStudents, fetchAnalytics, fetchSafety]);

  // ── Tabs ───────────────────────────────────────────────────────────────────
  const tabs = [
    { id: 'overview', label: 'Program Overview' },
    { id: 'participants', label: 'Participants' },
    { id: 'impact', label: 'Impact & Outcomes' },
    { id: 'safety', label: 'Safety' },
  ];

  // ── Loading / Error ────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: C.offWhite }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 60, height: 60, margin: '0 auto 24px', borderRadius: 16, backgroundColor: C.paleGray, animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite', border: `2px solid ${C.mintGray}` }} />
          <p style={{ fontSize: 16, color: C.brandBlue, fontWeight: 500 }}>Loading Program Dashboard...</p>
          <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.5} }`}</style>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: 40, textAlign: 'center', backgroundColor: C.offWhite, minHeight: '100vh' }}>
        <div style={{ maxWidth: 600, margin: '80px auto' }}>
          <h2 style={{ color: C.coral, marginBottom: 20 }}>Dashboard Error</h2>
          <p style={{ color: C.darkSlate, marginBottom: 30 }}>{error}</p>
          <button onClick={fetchOverview} style={{ padding: '12px 24px', backgroundColor: C.teal, color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600, marginRight: 10 }}>Retry</button>
          <button onClick={onLogout} style={{ padding: '12px 24px', backgroundColor: C.lightGray, color: C.darkSlate, border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 16, fontWeight: 600 }}>Logout</button>
        </div>
      </div>
    );
  }

  const d = overview || {};
  const school = d.school || {};
  const m = d.metrics || {};
  const t = d.teachers || {};
  const lt = d.learning_time || {};
  const li = d.learning_independence || {};
  const roi = d.roi || {};

  // ── Render helpers per tab ─────────────────────────────────────────────────

  const renderOverview = () => (
    <div>
      {/* Program header */}
      <div style={{ marginBottom: 32 }}>
        <h2 style={{ fontSize: 32, fontWeight: 600, color: C.darkTeal, marginBottom: 8, letterSpacing: '-0.5px' }}>
          {school.name || 'Metropolitan Ministries'}
        </h2>
        <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap' }}>
          <Badge label={school.license_status === 'active' ? 'Active Program' : 'Inactive'} color={school.license_status === 'active' ? C.skyBlue : C.coral} />
          <span style={{ fontSize: 14, color: C.brandBlue }}>Org: {school.district_id || 'Metro Ministries'}</span>
          <button onClick={fetchOverview} style={{ marginLeft: 'auto', padding: '8px 16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 14, color: '#374151' }}>Refresh</button>
        </div>
      </div>

      {/* Metrics Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 40 }}>
        <MetricCard
          value={`${m.seats_used || 0}/${m.seats_total || 0}`}
          label="Participants Enrolled"
          sublabel={`${m.seats_available || 0} slots available`}
          percentage={m.utilization_pct || 0}
          accent={C.teal}
        />
        <MetricCard
          value={`${m.active_students_pct || 0}%`}
          label="Active This Month"
          sublabel={`${m.active_students || 0} participants`}
          percentage={m.active_students_pct || 0}
          accent="#10b981"
        />
        <MetricCard
          value={`${lt.total_hours?.toLocaleString() || 0} hrs`}
          label="Learning Hours Delivered"
          sublabel={`${lt.avg_hours_per_student || 0} hrs per participant`}
          accent={C.brightTeal}
        />
        <MetricCard
          value={`${li.avg_score || 0}/100`}
          label="Self-Sufficiency Score"
          sublabel={independenceLabel(li.avg_score || 0).text}
          percentage={li.avg_score || 0}
          accent={independenceLabel(li.avg_score || 0).color}
        />
        <MetricCard
          value={`${t.active || 0}/${t.total || 0}`}
          label="Mentors Engaged"
          sublabel="Engagement rate"
          percentage={t.adoption_pct || 0}
          accent={C.aqua}
        />
        <MetricCard
          value={m.cost_per_student ? `$${m.cost_per_student}` : 'N/A'}
          label="Cost Per Participant"
          sublabel={m.annual_cost ? `Annual: $${m.annual_cost.toLocaleString()}` : ''}
          accent={C.gold}
        />
        <MetricCard
          value={roi.roi_multiple ? `${roi.roi_multiple}x` : 'N/A'}
          label="Impact Multiplier"
          sublabel={roi.equivalent_tutoring_value ? `$${roi.equivalent_tutoring_value.toLocaleString()} equivalent service value` : ''}
          accent={C.coral}
        />
      </div>

      {/* Funder Impact Narrative */}
      <SectionCard title="Funder Impact Summary" subtitle="Share this with grant reviewers and board members">
        <InfoBox color="green">
          <strong>Program Impact at a Glance:</strong> Your program has delivered <strong>{lt.total_hours?.toLocaleString() || 0} hours</strong> of
          personalized learning support to <strong>{m.seats_used || 0} participants</strong> at
          just <strong>${m.cost_per_student || 0} per participant</strong>.
          {roi.equivalent_tutoring_value > 0 && (
            <> This is equivalent to <strong>${roi.equivalent_tutoring_value.toLocaleString()}</strong> in private tutoring services—a <strong>{roi.roi_multiple}x return</strong> on your investment.</>
          )}
        </InfoBox>
        <InfoBox color="blue">
          <strong>Self-Sufficiency Outcomes:</strong> Participants average a <strong>{li.avg_score || 0}/100</strong> independence score,
          meaning they are {li.avg_score >= 70 ? 'actively self-directing their learning—demonstrating the program is building lasting capability, not dependency.'
            : li.avg_score >= 40 ? 'showing growth toward independent learning. The AI tutor is progressively building their confidence and self-direction skills.'
            : 'receiving the intensive support they need, with the AI adapting to meet them where they are. As they progress, independence scores will rise.'}
        </InfoBox>
      </SectionCard>

      {/* What Participants Are Learning */}
      <SectionCard title="Program Capabilities">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16 }}>
          {[
            { track: 'Youth Academic Support', desc: 'Homework help, concept explanation, curiosity building. AI adapts to each learner\'s style and pace.', color: C.teal },
            { track: 'ESL / Bilingual Bridge', desc: 'Spanish-first instruction with active English weaving. Builds confidence in both languages simultaneously.', color: C.brightTeal },
            { track: 'Workforce Readiness', desc: 'Resume building, interview practice, workplace communication, professional email writing, job applications.', color: C.coral },
            { track: 'Parent Empowerment', desc: 'Parent-teacher conference prep, school terminology, advocating for their child, understanding report cards.', color: C.gold },
            { track: 'Life Skills', desc: 'Budgeting, healthcare navigation, lease/contract understanding, digital literacy for work and parenting.', color: C.aqua },
          ].map(item => (
            <div key={item.track} style={{
              padding: '20px', borderRadius: '12px', backgroundColor: C.paleGray,
              borderLeft: `4px solid ${item.color}`,
            }}>
              <div style={{ fontWeight: 600, fontSize: '15px', color: C.darkTeal, marginBottom: '6px' }}>{item.track}</div>
              <div style={{ fontSize: '13px', color: C.brandBlue, lineHeight: '1.5' }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </SectionCard>

      {/* Quick Actions */}
      <SectionCard title="Quick Actions">
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button onClick={() => setActiveTab('impact')} style={{ padding: '12px 24px', backgroundColor: C.teal, color: 'white', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600, boxShadow: '0 2px 8px rgba(41,122,146,0.25)' }}>
            View Impact Report
          </button>
          <button onClick={() => setActiveTab('participants')} style={{ padding: '12px 24px', backgroundColor: C.paleGray, color: C.darkSlate, border: `1px solid ${C.lightGray}`, borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            View Participants
          </button>
          <button onClick={() => setActiveTab('safety')} style={{ padding: '12px 24px', backgroundColor: C.paleGray, color: C.darkSlate, border: `1px solid ${C.lightGray}`, borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 600 }}>
            Safety Reports
          </button>
        </div>
      </SectionCard>
    </div>
  );

  // ── Participants Tab ────────────────────────────────────────────────────────
  const renderParticipants = () => {
    if (!students) return <p style={{ color: C.brandBlue }}>Loading participants...</p>;

    const list = students.students || [];

    return (
      <div>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
          <div>
            <h2 style={{ fontSize: 24, fontWeight: 600, color: C.darkTeal, marginBottom: 4 }}>Participant Roster</h2>
            <p style={{ fontSize: 14, color: C.brandBlue }}>{students.total || list.length} total participants</p>
          </div>
          <button onClick={fetchStudents} style={{ padding: '8px 16px', backgroundColor: '#f3f4f6', border: '1px solid #d1d5db', borderRadius: 6, cursor: 'pointer', fontSize: 14, color: '#374151' }}>Refresh</button>
        </div>

        <SectionCard>
          {list.length === 0 ? (
            <p style={{ color: C.mauve, textAlign: 'center', padding: 40 }}>No participants enrolled yet. Participants will appear here after they log in.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
                <thead>
                  <tr style={{ borderBottom: `2px solid ${C.lightGray}` }}>
                    {['Participant', 'Program Track', 'Sessions', 'Hours', 'Last Active', 'Independence', 'Engagement'].map(h => (
                      <th key={h} style={{ padding: '12px 16px', textAlign: 'left', color: C.brandBlue, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {list.map((s, i) => {
                    const ind = independenceLabel(s.energy_score != null ? Math.round((1 - s.energy_score) * 100) : 50);
                    const track = s.grade_level || (s.preferences?.learner_type === 'adult' ? 'Adult Learner' : 'Youth');
                    return (
                      <tr key={s.id || i} style={{ borderBottom: `1px solid ${C.paleGray}` }}
                        onMouseEnter={e => e.currentTarget.style.backgroundColor = C.paleGray}
                        onMouseLeave={e => e.currentTarget.style.backgroundColor = 'transparent'}
                      >
                        <td style={{ padding: '14px 16px' }}>
                          <div style={{ fontWeight: 600, color: C.darkTeal }}>{s.first_name} {s.last_name}</div>
                          {s.username && <div style={{ fontSize: 12, color: C.mauve }}>@{s.username}</div>}
                        </td>
                        <td style={{ padding: '14px 16px' }}><Badge label={track} color={track === 'Adult Learner' ? C.coral : C.teal} /></td>
                        <td style={{ padding: '14px 16px', color: C.darkSlate }}>{s.session_count || 0}</td>
                        <td style={{ padding: '14px 16px', color: C.darkSlate }}>{s.total_hours ? s.total_hours.toFixed(1) : '0'}</td>
                        <td style={{ padding: '14px 16px', color: C.mauve, fontSize: 13 }}>{s.last_active || 'Never'}</td>
                        <td style={{ padding: '14px 16px' }}><Badge label={ind.text} color={ind.color} /></td>
                        <td style={{ padding: '14px 16px' }}>
                          <Badge label={s.engagement_level || 'Unknown'} color={s.engagement_level === 'High' ? '#10b981' : s.engagement_level === 'Medium' ? '#f59e0b' : '#ef4444'} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>

        <SectionCard style={{ backgroundColor: `${C.teal}08` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: C.darkTeal, marginBottom: 4 }}>Export Participant Roster</div>
              <div style={{ fontSize: 13, color: C.brandBlue }}>Download participant data for grant reporting and impact analysis</div>
            </div>
            <a href={`/api/admin/school/export/students?format=csv`} style={{ padding: '10px 20px', backgroundColor: C.teal, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>Download CSV</a>
          </div>
        </SectionCard>
      </div>
    );
  };

  // ── Impact & Outcomes Tab ──────────────────────────────────────────────────
  const renderImpact = () => {
    const a = analytics || {};
    const tq = teachingQuality;
    const rr = reteachRoi;

    return (
      <div>
        <div style={{ marginBottom: 32 }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: C.darkTeal, marginBottom: 6 }}>Program Impact & Outcomes</h2>
          <p style={{ fontSize: 14, color: C.brandBlue }}>Impact metrics demonstrating program effectiveness for funders and stakeholders</p>
        </div>

        {/* Top-level impact metrics */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 20, marginBottom: 32 }}>
          <MetricCard value={`${a.total_learning_hours?.toLocaleString() || lt.total_hours?.toLocaleString() || 0} hrs`} label="Total Learning Hours" sublabel="hours of personalized support" accent={C.teal} />
          <MetricCard value={a.topics_mastered || 0} label="Skills Developed" sublabel={`Avg mastery: ${a.avg_mastery_pct || 0}%`} accent="#10b981" />
          <MetricCard value={`${a.avg_hours_per_student || lt.avg_hours_per_student || 0} hrs`} label="Avg Hours Per Participant" accent={C.brightTeal} />
          <MetricCard value={a.total_questions || 0} label="Questions Asked" sublabel="participant questions" accent={C.aqua} />
        </div>

        {/* Program Impact Analysis (ROI reframed) */}
        <SectionCard title="Program Impact Analysis" subtitle="How your investment translates into participant outcomes">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, marginBottom: 16 }}>
            <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, textTransform: 'uppercase', marginBottom: 8 }}>Annual Program Cost</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.darkTeal }}>${(a.annual_cost || m.annual_cost || 0).toLocaleString()}</div>
              <div style={{ fontSize: 12, color: C.mauve }}>${m.cost_per_student || 0} per participant</div>
            </div>
            <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, textTransform: 'uppercase', marginBottom: 8 }}>Social Services Cost Offset</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>${(a.intervention_cost_avoided || (lt.total_hours || 0) * 70).toLocaleString()}</div>
              <div style={{ fontSize: 12, color: C.mauve }}>At $70/hr equivalent social worker/tutor time</div>
            </div>
            <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, textTransform: 'uppercase', marginBottom: 8 }}>Staff Capacity Freed</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.teal }}>{a.teacher_hours_saved || Math.round((lt.total_hours || 0) * 0.3)} hrs</div>
              <div style={{ fontSize: 12, color: C.mauve }}>Mentors can focus on high-touch case work</div>
            </div>
          </div>

          <InfoBox color="green">
            <strong>Funder-Ready Summary:</strong> Your program delivered <strong>{lt.total_hours?.toLocaleString() || 0} hours</strong> of
            personalized learning support to <strong>{m.seats_used || 0} participants</strong> at
            <strong> ${m.cost_per_student || 0} per participant</strong>—providing services that would cost
            <strong> ${((lt.total_hours || 0) * 70).toLocaleString()}</strong> through traditional social service providers.
            Tappy freed <strong>{Math.round((lt.total_hours || 0) * 0.3)} hours</strong> of staff capacity by providing
            always-available AI tutoring, allowing your mentors to focus on high-touch case work and relationship building.
          </InfoBox>
        </SectionCard>

        {/* Self-Sufficiency Outcomes */}
        <SectionCard title="Self-Sufficiency & Independence" subtitle="Measuring whether participants are building lasting learning capability">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 16 }}>
            <div style={{ textAlign: 'center', padding: 24, backgroundColor: C.paleGray, borderRadius: 12 }}>
              <div style={{ fontSize: 48, fontWeight: 700, color: independenceLabel(li.avg_score || 0).color }}>{li.avg_score || 0}</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.brandBlue, marginTop: 4 }}>AVG INDEPENDENCE SCORE</div>
              <div style={{ fontSize: 12, color: C.mauve }}>out of 100 · {li.total_students_measured || 0} participants measured</div>
            </div>
            <div style={{ padding: 24, backgroundColor: C.paleGray, borderRadius: 12 }}>
              <div style={{ fontWeight: 600, color: C.darkTeal, marginBottom: 12 }}>What This Measures</div>
              <div style={{ fontSize: 13, color: C.brandBlue, lineHeight: 1.8 }}>
                <div><strong>Exploration (30%)</strong> — Are they asking deeper questions?</div>
                <div><strong>Self-Direction (35%)</strong> — Are they learning without heavy guidance?</div>
                <div><strong>Topic Breadth (20%)</strong> — Are they exploring multiple subjects?</div>
                <div><strong>Question Quality (15%)</strong> — Are questions becoming more analytical?</div>
              </div>
            </div>
          </div>
          <InfoBox color="blue">
            <strong>Why This Matters for Funders:</strong> Unlike simple "hours served" metrics, the independence score
            measures whether your program is creating <em>lasting capability</em>. A rising score means participants
            are learning to learn—building skills they'll carry beyond this program. This is the difference between
            dependency-creating services and empowerment-focused programs.
          </InfoBox>
        </SectionCard>

        {/* Teaching Quality / AI Effectiveness */}
        {tq && (
          <SectionCard title="AI Tutoring Effectiveness" subtitle="How well the AI adapts to each participant's learning style">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 20, marginBottom: 16 }}>
              <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: (tq.avg_energy || 0) < 0.4 ? '#10b981' : (tq.avg_energy || 0) < 0.7 ? '#f59e0b' : '#ef4444' }}>
                  {tq.avg_energy != null ? tq.avg_energy.toFixed(2) : 'N/A'}
                </div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, marginTop: 4 }}>AVG ENERGY SCORE</div>
                <div style={{ fontSize: 11, color: C.mauve }}>Lower = more natural teaching</div>
              </div>
              <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: '#10b981' }}>{tq.effective_pct || 0}%</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, marginTop: 4 }}>EFFECTIVE ACTIONS</div>
                <div style={{ fontSize: 11, color: C.mauve }}>of {tq.total_actions || 0} teaching interactions</div>
              </div>
              <div style={{ padding: 20, backgroundColor: C.paleGray, borderRadius: 12, textAlign: 'center' }}>
                <div style={{ fontSize: 28, fontWeight: 700, color: C.teal }}>+{tq.avg_mastery_gain ? (tq.avg_mastery_gain * 100).toFixed(1) : '0'}%</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: C.brandBlue, marginTop: 4 }}>AVG SKILL GAIN</div>
                <div style={{ fontSize: 11, color: C.mauve }}>per teaching interaction</div>
              </div>
            </div>
            <InfoBox color="green">
              <strong>What This Means:</strong> The energy score shows how naturally the AI adapts to each participant.
              Lower scores mean the AI is finding effective teaching approaches quickly—it's not forcing methods that
              don't work. {(tq.avg_energy || 0) < 0.4 ? 'Your participants are responding well to the AI\'s approach.' : 'The AI is actively learning what works best for your participants.'}
            </InfoBox>
          </SectionCard>
        )}

        {/* Re-teach / Misconception Prevention */}
        {rr && rr.total_time_saved > 0 && (
          <SectionCard title="Proactive Support Outcomes" subtitle="Time saved by catching learning gaps early">
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
              <MetricCard value={`${rr.total_time_saved || 0} min`} label="Time Saved" sublabel={`${rr.students_helped || 0} participants helped`} accent="#10b981" />
              <MetricCard value={`${rr.avg_roi || 0}x`} label="Average ROI" sublabel="time saved vs invested" accent={C.teal} />
              <MetricCard value={rr.misconceptions_caught || 0} label="Gaps Caught" sublabel="last 30 days" accent={C.coral} />
            </div>
            <InfoBox color="green">
              <strong>Impact:</strong> Your program saved <strong>{rr.total_time_saved || 0} minutes</strong> this month by proactively
              addressing learning gaps. That's staff capacity redirected to direct participant support instead of
              repetitive re-teaching.
            </InfoBox>
          </SectionCard>
        )}

        {/* Common Questions */}
        {a.common_questions && a.common_questions.length > 0 && (
          <SectionCard title="Most Common Participant Questions" subtitle="What participants are asking the AI tutor">
            {a.common_questions.slice(0, 8).map((q, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', borderBottom: i < 7 ? `1px solid ${C.paleGray}` : 'none',
              }}>
                <span style={{ fontSize: 14, color: C.darkSlate }}>{q.question || q.text || q.topic}</span>
                <span style={{ fontSize: 13, color: C.mauve, whiteSpace: 'nowrap', marginLeft: 16 }}>{q.count || q.student_count} participants</span>
              </div>
            ))}
          </SectionCard>
        )}

        {/* Struggling Topics */}
        {a.struggling_topics && a.struggling_topics.length > 0 && (
          <SectionCard title="Areas Where Participants Need Support" subtitle="Topics with lower mastery scores across your program">
            {a.struggling_topics.slice(0, 6).map((t, i) => (
              <div key={i} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '12px 16px', backgroundColor: i % 2 === 0 ? '#fffbeb' : 'transparent',
                borderRadius: 8,
              }}>
                <span style={{ fontSize: 14, color: C.darkSlate }}>{t.topic}</span>
                <span style={{ fontSize: 13, color: C.mauve }}>Avg mastery: {t.avg_mastery || t.mastery_pct || 0}%</span>
              </div>
            ))}
            <InfoBox color="gold">
              These topics may benefit from targeted mentoring or additional program resources.
              Consider sharing this data with case workers and program leads.
            </InfoBox>
          </SectionCard>
        )}

        {/* Export */}
        <SectionCard style={{ backgroundColor: `${C.teal}08` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, color: C.darkTeal, marginBottom: 4 }}>Export Impact Report</div>
              <div style={{ fontSize: 13, color: C.brandBlue }}>Download comprehensive reports for funders and grant applications</div>
            </div>
            <a href="/api/admin/school/export/analytics?format=csv" style={{ padding: '10px 20px', backgroundColor: C.teal, color: 'white', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, textDecoration: 'none', marginRight: 8 }}>CSV Report</a>
            <a href="/api/admin/school/export/analytics?format=pdf" style={{ padding: '10px 20px', backgroundColor: C.paleGray, color: C.darkSlate, border: `1px solid ${C.lightGray}`, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600, textDecoration: 'none' }}>PDF Report</a>
          </div>
        </SectionCard>
      </div>
    );
  };

  // ── Safety Tab ──────────────────────────────────────────────────────────────
  const renderSafety = () => {
    if (!safety) return <p style={{ color: C.brandBlue }}>Loading safety data...</p>;

    const alerts = safety.alerts || [];
    const stats = safety.stats || {};

    return (
      <div>
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ fontSize: 24, fontWeight: 600, color: C.darkTeal, marginBottom: 6 }}>Safety & Content Moderation</h2>
          <p style={{ fontSize: 14, color: C.brandBlue }}>Monitor flagged content and ensure participant safety</p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Critical', count: stats.critical || 0, color: '#ef4444' },
            { label: 'High', count: stats.high || 0, color: C.coral },
            { label: 'Medium', count: stats.medium || 0, color: C.gold },
            { label: 'Low', count: stats.low || 0, color: '#6b7280' },
            { label: 'Total', count: stats.total || alerts.length, color: C.teal },
          ].map(s => (
            <div key={s.label} style={{
              padding: '16px 20px', backgroundColor: 'white', borderRadius: 12,
              border: `2px solid ${C.lightGray}`, borderTop: `4px solid ${s.color}`, textAlign: 'center',
            }}>
              <div style={{ fontSize: 28, fontWeight: 700, color: s.color }}>{s.count}</div>
              <div style={{ fontSize: 11, fontWeight: 600, color: C.brandBlue, textTransform: 'uppercase' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Alert list */}
        <SectionCard>
          {alerts.length === 0 ? (
            <div style={{ textAlign: 'center', padding: 40 }}>
              <div style={{ fontSize: 18, fontWeight: 600, color: '#10b981', marginBottom: 8 }}>All Clear</div>
              <p style={{ color: C.mauve }}>No content moderation alerts for this period.</p>
            </div>
          ) : (
            alerts.slice(0, 20).map((a, i) => (
              <div key={a.id || i} style={{
                padding: '16px 20px', borderBottom: `1px solid ${C.paleGray}`,
                borderLeft: `4px solid ${a.severity === 'CRITICAL' ? '#ef4444' : a.severity === 'HIGH' ? C.coral : a.severity === 'MEDIUM' ? C.gold : '#6b7280'}`,
                marginBottom: 8, borderRadius: '0 8px 8px 0',
              }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
                  <Badge label={a.severity} color={a.severity === 'CRITICAL' ? '#ef4444' : a.severity === 'HIGH' ? C.coral : a.severity === 'MEDIUM' ? C.gold : '#6b7280'} />
                  <Badge label={a.status || 'New'} color={C.brandBlue} />
                  <span style={{ fontSize: 12, color: C.mauve, marginLeft: 'auto' }}>{a.created_at || ''}</span>
                </div>
                {a.student_name && <div style={{ fontSize: 13, color: C.darkSlate, marginBottom: 4 }}>Participant: {a.student_name}</div>}
                {a.transcription && (
                  <div style={{ padding: '8px 12px', backgroundColor: '#fffbeb', borderRadius: 6, fontSize: 13, color: C.darkSlate, fontStyle: 'italic' }}>"{a.transcription}"</div>
                )}
              </div>
            ))
          )}
        </SectionCard>

        <InfoBox color="blue">
          <strong>About Content Moderation:</strong> Our AI-powered content moderation system monitors participant
          conversations in real-time to ensure safety. Alerts are automatically sent to relevant mentors via email.
          Program admins can review all alerts here for oversight and compliance purposes. All data is FERPA/COPPA compliant.
        </InfoBox>
      </div>
    );
  };

  // ── Main Render ────────────────────────────────────────────────────────────
  return (
    <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif' }}>
      {/* Header */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '20px 40px', backgroundColor: 'rgba(252,253,254,0.85)',
        backdropFilter: 'blur(20px) saturate(180%)', WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        borderBottom: `1px solid ${C.lightGray}`, position: 'sticky', top: 0, zIndex: 100,
        boxShadow: '0 1px 3px rgba(26,62,72,0.08)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <img src="/images/TAPPY-logo-black.png" alt="Teach Tappy Logo" style={{ height: 32, width: 'auto', objectFit: 'contain' }} />
          <div>
            <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, color: C.darkTeal, letterSpacing: '-0.5px' }}>
              {school.name || 'Program Dashboard'}
            </h1>
            <p style={{ margin: '4px 0 0', fontSize: 14, color: C.brandBlue }}>
              Program Administrator · {user?.email}
            </p>
          </div>
        </div>
        <button onClick={onLogout} style={{
          padding: '10px 20px', backgroundColor: C.paleGray, color: C.darkSlate,
          border: `1px solid ${C.lightGray}`, borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600,
        }}>Logout</button>
      </div>

      {/* Tab Navigation */}
      <div style={{
        display: 'flex', gap: 4, padding: '0 40px', backgroundColor: '#ffffff',
        borderBottom: `1px solid ${C.lightGray}`,
      }}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '14px 24px', backgroundColor: 'transparent', border: 'none',
              borderBottom: activeTab === tab.id ? `3px solid ${C.teal}` : '3px solid transparent',
              color: activeTab === tab.id ? C.teal : C.brandBlue,
              cursor: 'pointer', fontSize: 14, fontWeight: activeTab === tab.id ? 600 : 500,
              transition: 'all 200ms ease', letterSpacing: '-0.2px',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div style={{ padding: 40, backgroundColor: C.offWhite, minHeight: 'calc(100vh - 140px)' }}>
        {activeTab === 'overview' && renderOverview()}
        {activeTab === 'participants' && renderParticipants()}
        {activeTab === 'impact' && renderImpact()}
        {activeTab === 'safety' && renderSafety()}

        {/* Footer */}
        <div style={{
          marginTop: 60, backgroundColor: 'rgba(245,249,250,0.95)', backdropFilter: 'blur(10px)',
          borderTop: '1px solid rgba(213,222,221,0.5)', padding: '1rem', textAlign: 'center',
          fontSize: '0.65rem', color: '#76909f',
        }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.3rem', maxWidth: 600, margin: '0 auto' }}>
            <img src="/images/TAPPY-logo-black.png" alt="Tappy Logo" style={{ height: 18, width: 'auto', objectFit: 'contain', marginBottom: '0.2rem' }} />
            <p style={{ margin: 0, fontSize: '0.65rem', lineHeight: 1.4 }}>
              &copy; {new Date().getFullYear()} Teach Tappy | Powered by <span style={{ fontWeight: 500 }}>Morpheus RL</span>
            </p>
            <p style={{ margin: 0, fontSize: '0.6rem', lineHeight: 1.4 }}>
              By using our services, you agree to Tappy's{' '}
              <a href="/terms-of-service.html" target="_blank" rel="noopener noreferrer" style={{ color: '#76909f', textDecoration: 'underline' }}>Terms of Use</a>
              {' '}and{' '}
              <a href="/privacy-policy.html" target="_blank" rel="noopener noreferrer" style={{ color: '#76909f', textDecoration: 'underline' }}>Privacy Policy</a>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default MetroMinistriesDashboard;
